/**
 * EmployeeList — searchable, filterable list of all employees with drill-down.
 */

import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

interface EmployeeRow {
  EmployeeId: number;
  BakerId: string;
  LegalName: string;
  JobTitle: string | null;
  BusinessTitle: string | null;
  WorkEmail: string | null;
  ResolvedSiteCode: string | null;
  EmployeeCategory: string | null;
  HireDate: string | null;
  FullTimePartTime: string | null;
  BranchName: string | null;
  City: string | null;
  State: string | null;
}

async function fetchEmployees(params: Record<string, string | number>) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== '' && v !== undefined) qs.set(k, String(v));
  }
  const res = await fetch(`/api/employees?${qs.toString()}`, { credentials: 'include' });
  if (!res.ok) throw new Error('Failed to load employees');
  return res.json();
}

export function EmployeeList() {
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['employees', search, siteFilter, page],
    queryFn: () => fetchEmployees({ search, siteNumber: siteFilter, page, pageSize: 50 }),
  });

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  }, []);

  if (error) return <ErrorMessage message={(error as Error).message} onRetry={() => refetch()} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h1 className="m-0 text-lg font-semibold text-text-primary">Employees</h1>
        <Link
          to="/admin/data-validation"
          className="px-4 py-2 rounded-lg border border-border bg-white text-sm font-medium text-slate-600 no-underline hover:bg-page transition-all"
        >
          Data Validation Report
        </Link>
      </div>

      <form onSubmit={handleSearch} className="flex gap-3 mb-5 flex-wrap items-end">
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Search</label>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Name, email, or title…"
            className="py-2 px-3 rounded-lg border border-border text-sm w-64 bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all placeholder:text-slate-400"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 mb-1">Site #</label>
          <input
            type="text"
            value={siteFilter}
            onChange={(e) => { setSiteFilter(e.target.value); setPage(1); }}
            placeholder="e.g. 153"
            maxLength={3}
            className="py-2 px-3 rounded-lg border border-border text-sm w-20 bg-white focus:outline-none focus:ring-2 focus:ring-baker-accent/40 focus:border-baker-accent transition-all placeholder:text-slate-400"
          />
        </div>
      </form>

      {isLoading ? (
        <LoadingSpinner message="Loading employees…" />
      ) : (
        <>
          <div className="text-xs text-slate-500 mb-2">{data?.total ?? 0} employees</div>
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b-2 border-border bg-page/50">
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Name</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Title</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Email</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Site</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Location</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Category</th>
                </tr>
              </thead>
              <tbody>
                {(data?.data as EmployeeRow[] ?? []).map((emp) => (
                  <tr key={emp.EmployeeId} className="border-b border-border-subtle hover:bg-page/50 transition-colors">
                    <td className="py-2 px-3">
                      <Link to={`/employees/${emp.EmployeeId}`} className="text-baker-red no-underline hover:underline font-medium">
                        {emp.LegalName}
                      </Link>
                    </td>
                    <td className="py-2 px-3 text-slate-600">{emp.BusinessTitle ?? emp.JobTitle ?? '—'}</td>
                    <td className="py-2 px-3">
                      {emp.WorkEmail ? (
                        <a href={`mailto:${emp.WorkEmail}`} className="text-slate-600 no-underline hover:text-baker-red">{emp.WorkEmail}</a>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-3">
                      {emp.ResolvedSiteCode ? (
                        <Link to={`/sites/${emp.ResolvedSiteCode}`} className="text-baker-red no-underline hover:underline">
                          {emp.ResolvedSiteCode}
                        </Link>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-3 text-slate-500 text-xs">
                      {emp.BranchName ? `${emp.BranchName}${emp.City ? `, ${emp.City}` : ''}${emp.State ? ` ${emp.State}` : ''}` : '—'}
                    </td>
                    <td className="py-2 px-3">
                      {emp.EmployeeCategory ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{emp.EmployeeCategory}</span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg border border-border bg-white text-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-page transition-all">
                ← Prev
              </button>
              <span className="px-3 py-1.5 text-xs text-slate-500">Page {page} of {data.totalPages}</span>
              <button onClick={() => setPage(p => Math.min(data.totalPages, p + 1))} disabled={page >= data.totalPages}
                className="px-3 py-1.5 rounded-lg border border-border bg-white text-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-page transition-all">
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
